import { getNewChatSound } from "@/helpers/audio";
import { dateToTicks } from "@/helpers/dateHelper";
import generateId, { isNullOrEmptyGuid } from "@/helpers/guid";

import * as LOG from "@/log";
import { getMessages, sendBasicEvent, sendCallbackRequest, sendMessage } from "@/services/TicketsService";
import ChatWidgetRtcHub from "@/services/rtc/ChatWidgetRtcHub";
import { getCWSettings } from "@/services/SettingsService";
import { AddBasicTicketEventRequest, BasicTicketEvent, CallbackRequest, ChatWidgetActionRequest, ChatWidgetPublicSettingsData, ChatWidgetTicketInfoRequest, ChatWidgetTypingRequest, CommunicationDirection, SendRequest, TypingCommunicatorData, TypingUserData } from "@/TickApi";
import { enGB } from "date-fns/locale";
import { defineStore, _ActionsTree, _GettersTree } from "pinia";
import useViewstateStore, { InterfaceStateType } from "./viewstate";
import { debug } from "@/log";
import { addFont } from "@/helpers/fontHelper";
import { deviceIsMobile } from "@/helpers/deviceHelper";
0

interface MessageDraft {
  message: string;
  typingUsers?: TypingUserData[];
  typingCommunicators?: TypingCommunicatorData[];
}

interface State {
  bootDate: number;
  bootExpirationDate: number;
  rtcConnectionId: string | null;
  cwToken: string | null;
  deviceAddress: string | null;
  chatWidgetId: string | null;
  rtcConnected: boolean;
  draft: MessageDraft;
  removeTypingUsersAtReceival: boolean;
  messages: ChatWidgetActionRequest[];
  cwSettings: ChatWidgetPublicSettingsData;
  lastMessageReceivedTick: number;
  ticketId: string | null;
  notifyIncomingMessages: boolean;
  deviceShowFullScreen: boolean
}

interface Getters extends _GettersTree<State> {
  connected(state: State): boolean;
  ready(state: State): boolean;
  locale: (state: State) => Locale;
}
interface Actions extends _ActionsTree {
  boot(cwToken: string, forceRefresh: boolean): void;
  use(): void;
  send(metadataObject: object, actionId?: string, messageNotUsingDraft?: string): void;
  sendBasicEvent(data: BasicTicketEvent): void;

  replyOptionCallback(callbackId: string): void;
  patchMessage(isOptimisticPreview: boolean, message: ChatWidgetActionRequest, disableNotifications: boolean): void;
  patchActionData(serverData: ChatWidgetTypingRequest): void;
  patchTicketInfo(serverData: ChatWidgetTicketInfoRequest): void;
  pollUpdates(): void;
  onWindowResize(): void;

}

const useAppStore = defineStore<"app", State, Getters, Actions>("app", {
  state: () => ({
    deviceAddress: null,
    bootDate: null,
    bootExpirationDate: null,
    notifyIncomingMessages: true,
    rtcConnectionId: null,
    rtcConnected: false,
    cwToken: null,
    cwSettings: {
      opener: {
        styling: {},
      },
      conversation: {
        buttonsStyle: {},
        inboundMessageStyle: {},
        messageActions: {},
        messageBoxStyle: {},
        outboundMessageStyle: {}
      },
      lobby: {
        actions: [],
        actionsStyling: {},
        message: "",
        title: "",
      },
      maxHeightPercentage: 50,
      styling: {},
    },
    chatWidgetId: null,
    messages: [],
    draft: {
      message: "",
      typingUsers: [],
      typingCommunicators: [],
    },
    removeTypingUsersAtReceival: false,
    lastMessageReceivedTick: 0,
    ticketId: null,
    deviceShowFullScreen: false,
  }),
  getters: {
    ready: (state) => (state.cwToken != null && state.cwSettings != null),
    connected: (state) => state.rtcConnected,
    isPreview: (state) => (state.cwToken == "preview"),
    locale: () => {
      return enGB;
    },
  },
  actions: {
    onWindowResize() {
      this.deviceShowFullScreen = deviceIsMobile();
    },
    ensureDeviceId() {
      if (!this.deviceAddress) {
        this.deviceAddress = `CWID: ${generateId()}`;
      }
    },
    async boot(cwToken: string, forceRefresh: boolean) {
      if (cwToken == "null") { cwToken = null; }

      const isHardReloading = window.performance?.navigation?.type === 1;
      if (forceRefresh) {
        forceRefresh = true;
      }


      this.onWindowResize();
      //console.log("[BOOT:START]Booting up for cwToken " + cwToken);
      this.ensureDeviceId();

      const nowUtcTicks = new Date().getTime();
      if (!cwToken) {
        LOG.error("No cwToken present", {})
        return;
      }

      const needsBoot = (!this.bootExpirationDate || this.bootExpirationDate < nowUtcTicks);
      const secondsLeft = (this.bootExpirationDate - nowUtcTicks) / 1000;
      // console.log("needs reboot: ", needsBoot)
      // console.log("now: ", nowUtcTicks)
      // console.log("exp: ", this.bootExpirationDate)
      // console.log("seconds to expiration: ", secondsLeft)

      if (!needsBoot && !forceRefresh) {
        addFont(this.cwSettings?.fontFamily);

        if (cwToken == this.cwToken && this.cwSettings != null && this.cwSettings.styling.backgroundColor != null) {
          //LOG.warn("already booted for this cwToken")
          return;
        }
      }

      debug("cwToken changed to " + cwToken);

      const maxMinutesUntilReboot = 5;
      const bootExpiration = new Date(nowUtcTicks + maxMinutesUntilReboot * 60000).getTime();
      this.bootDate = nowUtcTicks;
      this.bootExpirationDate = bootExpiration;
      if (cwToken != this.cwToken) {
        this.cwToken = cwToken;
      }

      if (!this.chatWidgetId) {
        this.chatWidgetId = `${this.cwToken}:${this.deviceAddress}`;
      }



      if (!this.isPreview) {
        getCWSettings(this.cwToken, forceRefresh).then((serverSettings) => {
          this.cwSettings = serverSettings;
          addFont(serverSettings?.styling?.fontFamily);
        });
      } else {
        addFont(this.cwSettings?.styling?.fontFamily);
      }

    },
    async use() {

      if (!this.isPreview) {
        ChatWidgetRtcHub.subscribe("ChatWidget");
      }
    },
    async pollUpdates() {
      const hasRTC = useAppStore().rtcConnected;

      const hasTicketId = !isNullOrEmptyGuid(this.ticketId);

      if (hasTicketId && this.cwToken && this.chatWidgetId) {
        if (!hasRTC) {
          LOG.debug(`[Ticket:POLL] NO RTC => Poll for updates`)

          await getMessages(this.ticketId, this.cwToken, this.chatWidgetId, this.lastMessageReceivedTick).then((messages: ChatWidgetActionRequest[]) => {
            for (let index = 0; index < messages.length; index++) {
              this.patchMessage(false, messages[index], true);
            }
          });

        } else {
          LOG.debug(`[Tickeyt:POLL] No poll needed, RTC is active`)
        }
      }
    },
    async patchTicketInfo(serverData: ChatWidgetTicketInfoRequest) {
      this.ticketId = serverData.ticketId;

    },

    async patchActionData(data: ChatWidgetTypingRequest) {
      const draft = (this.draft as MessageDraft);
      draft.typingCommunicators = data.typingCommunicators;
      draft.typingUsers = data.typingUsers;

      if (data.autoRemoveAfterReceiveMessage) {
        this.removeTypingUsersAtReceival = true;
      }

    },
    async patchMessage(isOptimisticPreview: boolean, serverData: ChatWidgetActionRequest, disableNotifications: boolean) {
      if (!serverData) { return; }



      if (!isOptimisticPreview) {
        if (this.removeTypingUsersAtReceival) {
          const draft = (this.draft as MessageDraft);
          draft.typingUsers = [];
        }

        if (serverData.sentDateAsTick > this.lastMessageReceivedTick) {
          this.lastMessageReceivedTick = serverData.sentDateAsTick;
        }

        if (serverData.ticketId && this.ticketId != serverData.ticketId) {
          const hasTicketid = !isNullOrEmptyGuid(serverData.ticketId);
          if (hasTicketid) {
            LOG.debug("[Ticket:SET]SET ticketId")
            this.ticketId = serverData.ticketId;
          }
        }
      }

      const index = this.messages.findIndex(s => s.messageId === serverData.messageId);
      if (index === -1) {

        if (!disableNotifications) {
          if (this.notifyIncomingMessages && (!document.hasFocus() || !useViewstateStore().has(InterfaceStateType.ShowWidget))) {
            LOG.debug("[MESSAGE:Ping]Ping (sound)")
            const audio = new Audio(getNewChatSound());
            audio.play()
          }
        }

        this.messages.push(serverData);
      } else {
        this.messages.splice(index, 1, serverData);
      }
    },
    async replyOptionCallback(callbackId: string) {
      if (this.isPreview) { return; }
      const sendData: CallbackRequest = {
        callbackId: callbackId,
      };
      sendCallbackRequest(sendData, this.cwToken, this.chatWidgetId);
    },

    async sendBasicEvent(data?: BasicTicketEvent) {
      this.ensureDeviceId();
      if (this.isPreview) { return; }
      if (!data) { return; }

      const hasTicketId = !isNullOrEmptyGuid(this.ticketId);
      if (!hasTicketId) { return; }

      const request: AddBasicTicketEventRequest = {
        to: this.cwToken,
        chatWidgetId: this.chatWidgetId,
        ticketId: this.ticketId,
        eventData: data,
      }

      sendBasicEvent(request);
    },
    async send(metadataObject?: object, actionId?: string, messageNotUsingDraft?: string) {
      this.ensureDeviceId();
      if (messageNotUsingDraft == null && !this.draft.message && !actionId) { return; }


      const sendData: SendRequest = {
        messageId: generateId(),
        messageMarkDown: messageNotUsingDraft || this.draft.message,
        to: this.cwToken,
        chatWidgetId: this.chatWidgetId,
        from: this.deviceAddress,
        actionId: actionId,
        metaDataJson: metadataObject ? JSON.stringify(metadataObject) : null,
      };

      const now = new Date();
      // store locally
      const newItem: ChatWidgetActionRequest = {
        sentDateAsTick: dateToTicks(now),
        sentDate: now,
        chatWidgetId: sendData.chatWidgetId,
        messageId: sendData.messageId,
        messageHtml: sendData.messageMarkDown,
        ticketId: "",
        direction: CommunicationDirection.Inbound, // Tick's perspective
        from: this.deviceAddress,
        senderInfo: {
          name: "",
          avatarUrl: "",
          senderId: sendData.chatWidgetId,
        }
      }

      this.patchMessage(true, newItem, true);

      // if from action, already know Tick will probably respond
      if (actionId) {
        const typingInfo: ChatWidgetTypingRequest = {
          id: generateId(),
          autoRemoveAfterReceiveMessage: true,
          ticketId: "",
          typingUsers: [{
            name: "Someone",
            userId: "",
          }]
        }
        this.patchActionData(typingInfo);


      }



      // reset draft if used
      if (!messageNotUsingDraft) {
        this.draft.message = "";
      }

      if (this.isPreview) { return; }
      //ChatWidgetRtcHub.send(sendData)
      await sendMessage(sendData).then((serverData) => {
        this.patchMessage(false, serverData, false);
      });


    },
  },
  persist: {
    enabled: true,
    strategies: [
      //   {
      //   key: "tick-chat-widget",
      //   storage: window.sessionStorage
      // },

      {
        paths: ["bootDate"],
        key: "tick-chat-widget-bootDate",
        storage: window.sessionStorage,
      },
      {
        paths: ["bootExpirationDate"],
        key: "tick-chat-widget-bootExpirationDate",
        storage: window.sessionStorage,
      },

      {
        paths: ["rtcConnectionId"],
        key: "tick-chat-widget-rtcConnectionId",
        storage: window.sessionStorage,
      },

      {
        paths: ["cwToken"],
        key: "tick-chat-widget-cwToken",
        storage: window.sessionStorage,
      },
      {
        paths: ["chatWidgetId"],
        key: "tick-chat-widget-chatWidgetId",
        storage: window.sessionStorage,
      },
      {
        paths: ["rtcConnected"],
        key: "tick-chat-widget-rtcConnected",
        storage: window.sessionStorage,
      },
      {
        paths: ["draft"],
        key: "tick-chat-widget-draft",
        storage: window.sessionStorage,
      },
      {
        paths: ["messages"],
        key: "tick-chat-widget-messages",
        storage: window.sessionStorage,
      },
      {
        paths: ["cwSettings"],
        key: "tick-chat-widget-cwSettings",
        storage: window.sessionStorage,
      },
      {
        paths: ["lastMessageReceivedTick"],
        key: "tick-chat-widget-lastMessageReceivedTick",
        storage: window.sessionStorage,
      },
      {
        paths: ["ticketId"],
        key: "tick-chat-widget-ticketId",
        storage: window.sessionStorage,
      },

      {
        paths: ["notifyIncomingMessages"],
        key: "tick-chat-widget-notifyIncomingMessages",
        storage: window.sessionStorage,
      },
      {
        paths: ["deviceAddress"],
        key: "tick-chat-widget-deviceAddress",
        storage: window.localStorage,
      },


    ]
  }
});

export default useAppStore;
